import { Inject, Injectable } from '@angular/core';
import { HttpHeaders, HttpParams, HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import type { ConfigurationEndpoint } from './configuration.domain';

@Injectable({
  providedIn: 'root'
})
export class ConfigurationService {

  private config?: Record<string, unknown>;

  private loadPromise: Promise<Record<string, unknown>>;

  constructor(@Inject(HttpClient) private readonly http: HttpClient) {
  }

  /**
   * Returns complete object as it was received from server
   * Useful for preventing embedded trading from calling configuration again
   * and having to fight with PCoE guys about it :)
   */
  getAll(): Record<string, unknown> | undefined {
    return this.config;
  }

  /**
   * Calls remote configuration endpoint and initializes configuration data
   *
   * @param request Configuration endpoint to http.get the data
   * @param defaultValue fallback configuration object in case of error
   * @returns
   */
  load(request: ConfigurationEndpoint, defaultValue?: Record<string, unknown>): Promise<Record<string, unknown>> {
    if (!this.isLoaded()) {
      const url = request.url.replace('${applicationId}', request.applicationId);

      let options: { [key: string]: unknown };
      if (request.headers !== undefined || request.params !== undefined) {
        options = {};
        options.headers = request.headers !== undefined ? new HttpHeaders(request.headers) : undefined;
        options.params = request.params !== undefined ? new HttpParams({ fromObject: request.params }) : undefined;
      }
      this.loadPromise = lastValueFrom<Record<string, unknown>>(this.http.get<Record<string, unknown>>(url, options));
      return this.loadPromise.then(
        (config: Record<string, unknown>): Record<string, unknown> => {
          this.config = config.payload !== undefined ? config.payload as Record<string, unknown> : config;
          if (config.locationId !== undefined) {
            this.config.locationId = config.locationId;
          }
          return this.config;
        }, (error: unknown): Promise<Record<string, unknown>> => {
          this.config = defaultValue;
          return Promise.reject(error);
        });
    }
    return this.loadPromise;
  }

  /**
   * @param key Configuration Key. it can contain dot to refer no the nested properties.
   * @param defaultValue  Value to return when requested property is undefined.
   * @returns
   */
  getProperty<T>(key: string, defaultValue?: unknown | undefined): T | undefined {
    let result;
    if (key !== undefined && key.indexOf('.') > 0) {
      result = key.split('.').reduce((value: unknown, part: string): unknown => value && value[part], this.config);
    } else {
      result = this.config ? this.config[key] : undefined;
    }
    return result ?? defaultValue;
  }

  private isLoaded(): boolean {
    return this.loadPromise !== undefined && this.config !== undefined && Object.keys(this.config).length !== 0;
  }
}
